import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { File, Check, RotateCcw, Folder } from 'lucide-react';
import { Resource, YtdFile, JobState, OptimizeSettings } from './types';
import { cn } from '@/lib/utils';
import BottomActionBar from './BottomActionBar';

const CARD_H   = 80;
const CARD_GAP = 7;
const ROW_H    = CARD_H + CARD_GAP;
const OVERSCAN = 2;

interface Props {
  resource: Resource;
  jobState: JobState;
  selectedFiles: Set<string>;
  settings: OptimizeSettings;
  onSelectionChange: (next: Set<string>) => void;
  onSettingsChange: (patch: Partial<OptimizeSettings>) => void;
  onOptimizeAll: () => void;
  onOptimizeSelected: () => void;
  onCancel: () => void;
  onRunAgain: () => void;
  onBack: () => void;
}

function useColumns(containerRef: React.RefObject<HTMLDivElement | null>) {
  const [cols, setCols] = useState(6);
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setCols(entry.contentRect.width >= 1280 ? 6 : 4);
    });
    ro.observe(containerRef.current);
    setCols(containerRef.current.getBoundingClientRect().width >= 1280 ? 6 : 4);
    return () => ro.disconnect();
  }, [containerRef]);
  return cols;
}

export default function FilesDrillView({
  resource, jobState, selectedFiles, settings,
  onSelectionChange, onSettingsChange,
  onOptimizeAll, onOptimizeSelected, onCancel, onRunAgain, onBack,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapRef   = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH,     setViewH]     = useState(500);
  const columns = useColumns(wrapRef);

  const files: YtdFile[] = resource.ytdFiles ?? [];
  const oversizedSet = new Set(resource.oversizedFiles.map(f => f.name));

  const fileIndexMap = useMemo(
    () => new Map(files.map((f, i) => [f.name, i])),
    [files]
  );

  const totalRows   = Math.ceil(files.length / columns);
  const firstRow = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const lastRow  = Math.min(totalRows - 1, Math.ceil((scrollTop + viewH) / ROW_H) + OVERSCAN);

  const topPad    = firstRow * ROW_H;
  const bottomPad = Math.max(0, (totalRows - lastRow - 1) * ROW_H);

  const visibleFiles = files.slice(firstRow * columns, (lastRow + 1) * columns);

  useEffect(() => {
    if (!scrollRef.current) return;
    const ro = new ResizeObserver(([e]) => setViewH(e.contentRect.height));
    ro.observe(scrollRef.current);
    return () => ro.disconnect();
  }, []);

  const handleScroll = useCallback(() => {
    setScrollTop(scrollRef.current?.scrollTop ?? 0);
  }, []);

  const toggleFile = (name: string) => {
    const next = new Set(selectedFiles);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onSelectionChange(next);
  };

  const allSelected = files.length > 0 && selectedFiles.size === files.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(files.map(f => f.name)));
    }
  };

  const isRunning = jobState.active || jobState.status === 'running' || jobState.status === 'starting';
  const isDone    = jobState.status === 'success';

  function fileStatus(name: string): 'processing' | 'done' | 'queued' | 'idle' {
    if (!isRunning && !isDone) return 'idle';
    if (isDone) return 'done';
    if (jobState.currentFile === name) return 'processing';
    const currentIdx = jobState.currentFile ? (fileIndexMap.get(jobState.currentFile) ?? -1) : -1;
    const myIdx = fileIndexMap.get(name) ?? -1;
    if (currentIdx > -1 && myIdx < currentIdx) return 'done';
    return 'queued';
  }

  const selectedSizeMb = files
    .filter(f => selectedFiles.has(f.name))
    .reduce((sum, f) => sum + f.sizeMb, 0);

  const oversizedCount = resource.oversizedFiles.length;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 px-6 py-3 border-b border-white/[0.04] text-[11px] shrink-0" style={{ background: '#0c0f18' }}>
        <Folder size={13} className="text-slate-600 shrink-0" />
        <button
          onClick={onBack}
          disabled={isRunning}
          className="font-semibold text-slate-500 hover:text-slate-200 transition-colors underline underline-offset-2 decoration-slate-700 disabled:pointer-events-none disabled:opacity-40"
        >
          resources
        </button>
        <span className="text-slate-700">›</span>
        <span className="font-bold text-slate-200">{resource.name}</span>
        <span className="ml-auto text-[10px] text-slate-600 font-semibold">
          {files.length.toLocaleString()} files · {resource.sizeMb >= 1024 ? `${(resource.sizeMb / 1024).toFixed(1)} GB` : `${resource.sizeMb} MB`}
        </span>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 pt-3 pb-2 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">YTD Files</span>
          {oversizedCount > 0 && (
            <span className="text-[8.5px] font-bold uppercase bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full px-2 py-0.5">
              {oversizedCount} Oversized
            </span>
          )}
          {isDone && (
            <span className="text-[8.5px] font-bold uppercase bg-[#00ff66]/[0.08] text-[#00ff66] border border-[#00ff66]/15 rounded-full px-2 py-0.5">
              All Done
            </span>
          )}
        </div>
        {!isRunning && (
          <button
            onClick={toggleSelectAll}
            className="text-[9px] font-bold uppercase tracking-wider text-[#00ffd1] bg-[#00ffd1]/[0.06] border border-[#00ffd1]/20 rounded-full px-3 py-1 hover:bg-[#00ffd1]/[0.10] transition-colors"
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      {/* Virtualised scroll container */}
      <div ref={wrapRef} className="flex-1 min-h-0 px-6 overflow-hidden">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="overflow-y-auto"
          style={{ height: 'calc(100vh - 280px)' }}
        >
          <div style={{ height: topPad }} />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gap: `${CARD_GAP}px`,
            }}
          >
            {visibleFiles.map(file => {
              const isOversized = oversizedSet.has(file.name);
              const isChecked   = selectedFiles.has(file.name);
              const status      = fileStatus(file.name);

              return (
                <div
                  key={file.name}
                  onClick={() => !isRunning && toggleFile(file.name)}
                  style={{ height: CARD_H }}
                  className={cn(
                    'relative rounded-[10px] border p-2.5 transition-all duration-100 overflow-hidden',
                    !isRunning && 'cursor-pointer',
                    status === 'done'       && 'border-[#00ff66]/20 bg-[#00ff66]/[0.03]',
                    status === 'processing' && 'border-[#00ffd1]/30 bg-[#00ffd1]/[0.04]',
                    status === 'queued'     && 'opacity-50',
                    status === 'idle' && isOversized && !isChecked && 'border-amber-500/25 bg-amber-500/[0.04]',
                    status === 'idle' && isOversized &&  isChecked && 'border-amber-500/50 bg-amber-500/[0.07]',
                    status === 'idle' && !isOversized && isChecked && 'border-[#00ffd1]/30 bg-[#00ffd1]/[0.03]',
                    status === 'idle' && !isOversized && !isChecked && 'border-white/[0.04] bg-[#11141c] hover:border-white/[0.10]',
                  )}
                >
                  {status === 'processing' && (
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: 'linear-gradient(90deg,transparent,rgba(0,255,209,0.06),transparent)',
                        animation: 'shimmer 1.5s infinite',
                      }}
                    />
                  )}

                  <div className="absolute top-1.5 right-1.5">
                    {status === 'done' ? (
                      <div className="w-4 h-4 rounded-[3px] bg-[#00ff66]/15 flex items-center justify-center">
                        <Check size={9} className="text-[#00ff66]" />
                      </div>
                    ) : status === 'processing' ? (
                      <div className="w-4 h-4 rounded-[3px] bg-[#00ffd1]/12 flex items-center justify-center">
                        <RotateCcw size={9} className="text-[#00ffd1] animate-spin" />
                      </div>
                    ) : (
                      <div className={cn(
                        'w-4 h-4 rounded-[3px] border flex items-center justify-center',
                        isChecked ? 'bg-[#00ffd1] border-[#00ffd1]' : 'bg-black/50 border-white/[0.12]',
                      )}>
                        {isChecked && <Check size={9} className="text-[#0a0f1a]" />}
                      </div>
                    )}
                  </div>

                  <File
                    size={16}
                    className="mb-1"
                    style={{
                      color: status === 'done' ? '#00ff66'
                           : status === 'processing' ? '#00ffd1'
                           : isOversized ? '#f59e0b'
                           : '#475569',
                      opacity: status === 'done' ? 0.6 : 1,
                    }}
                  />

                  <div className={cn(
                    'text-[9.5px] font-bold leading-tight mb-1 break-all',
                    status === 'queued' ? 'text-slate-600' : 'text-slate-200',
                  )}>
                    {file.name}
                  </div>

                  <div className="text-[8.5px]">
                    {status === 'done' ? (
                      <span className="font-bold text-[#00ff66] bg-[#00ff66]/10 rounded px-1">✓ done</span>
                    ) : isOversized ? (
                      <span className="font-bold text-amber-500 bg-amber-500/10 rounded px-1 uppercase">{file.sizeMb} MB</span>
                    ) : (
                      <span className="text-slate-500">{file.sizeMb} MB</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ height: bottomPad + 8 }} />
        </div>
      </div>

      <BottomActionBar
        jobState={jobState}
        selectedCount={selectedFiles.size}
        totalCount={files.length}
        selectedSizeMb={selectedSizeMb}
        settings={settings}
        onSettingsChange={onSettingsChange}
        onOptimizeAll={onOptimizeAll}
        onOptimizeSelected={onOptimizeSelected}
        onCancel={onCancel}
        onRunAgain={onRunAgain}
        onBack={onBack}
      />
    </div>
  );
}
